import React, {
    useEffect,
    useState,
} from 'react';

import axios from 'axios';
import * as localforage from 'localforage';
import filter from 'lodash/filter';
import orderBy from 'lodash/orderBy';
import values from 'lodash/values';
import { normalize } from 'normalizr';
import { useDebounce } from 'use-debounce';
import parse from 'html-react-parser';

import LoadingAnimation from '../../components/LoadingAnimation';
import Config from '../../config';
import icons from '../../icons';
import SearchInput from '../ConversationsList/SearchInput';
import Filter from './Filter';
import {
    initialFilter,
    listSchema,
    SMARTLISTFILTERS,
    SMARTLISTTYPES,
} from './reducer';
import SmartListItem from './SmartListItem';

const SALES_REP_ROLES = ['0', '1', '3', '4', '7', '10'];

const SmartListComponent = ({ translate, state, dispatchState, session, filters, dispatchFilters }) => {
    const [perPage, setPerPage] = useState('100');
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [bookmarkedPage, setBookmarkedPage] = useState(1);
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue] = useDebounce(searchValue, 500);
    const [filterOpen, setFilterOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState(filters !== initialFilter);
    const [filterOptions, setFilterOptions] = useState([]);
    const [initialLoad, setInitialLoad] = useState(1);
    const [hideHeader, setHideHeader] = useState(true);
    const [currentAbortController, setCurrentAbortController] = useState(null);

    let abortController;

    const loadData = async (take, limit, name) => {

        if (currentAbortController) {
            currentAbortController.abort();
        }

        abortController = new AbortController();
        const newSignal = abortController.signal;
        setCurrentAbortController(abortController);

        const token = await localforage.getItem('token');
        setLoading(true);

        axios.get(
            `${Config.smartlist_url}`,
            {
                params: {
                    filter: 1,
                    ...filters,
                    iDisplayStart: (take * limit) - limit,
                    iDisplayLength: limit,
                    search_name: name
                },
                headers: {
                    Authorization: `Bearer ${token}`
                },
                signal: newSignal
            },
        )
            .then(resp => {
                setData(resp.data);
                setLoading(false);
                setHideHeader(false);
            })
            .catch(err => {
                setLoading(false);
            });
    };

    const loadFilterOptions = async () => {
        const token = await localforage.getItem('token');

        axios.get(
            `${Config.smartlist_filter_url}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )
            .then((resp: any) => {
                resp.data.filters.forEach(filterItem => {
                    if (filterItem.key === 'filterByRep') {
                        const found = filter(values(filterItem.data), o => o.sales_rep_id === session.sales_rep_id);
                        if (found.length === 1 && SALES_REP_ROLES.indexOf(session.role) > -1) {
                            dispatchFilters({
                                type: SMARTLISTFILTERS.UPDATE,
                                payload: {
                                    ...filters,
                                    filterByRep: session.sales_rep_id
                                }
                            });
                        }
                    }
                });

                setFilterOptions(resp.data.filters);
            })
            .catch(err => console.error('err', err));
    };

    const setData = (data) => {
        if (data && data.data) {
            const normalizedData = normalize(data.data, [listSchema]);
            dispatchState({
                type: SMARTLISTTYPES.ADD,
                data: normalizedData.entities.smartlist
            });
        }

        if (data && data.totals) {
            const limit = Number(perPage);
            const totalPages = Math.ceil(Number(data.totals) / limit);
            setPages(totalPages);
        }
    };

    useEffect(() => {
        loadFilterOptions();
    }, []);

    useEffect(() => {
        setInitialLoad(0);
    }, []);

    useEffect(() => {
        loadData(currentPage, Number(perPage), debouncedSearchValue);
    }, [filters]);

    // this one here will take a page user was on before entering the search value and will retun user to the same page
    // once the search value is cleared.
    useEffect(() => {
        if (debouncedSearchValue && debouncedSearchValue.trim() !== '') {
            setBookmarkedPage(currentPage);
            setCurrentPage(1);
            loadData(1, Number(perPage), debouncedSearchValue);
        }
        if (debouncedSearchValue === '' && !initialLoad) {
            setCurrentPage(bookmarkedPage);
            loadData(bookmarkedPage, Number(perPage), searchValue);
            setBookmarkedPage(1);
        }
    }, [debouncedSearchValue]);

    const filteredData = () => {
        const sortedData = orderBy(values(state), ['timestamp'], ['desc']);
        return sortedData;
    };

    const handlePerPageChange = e => {
        setPerPage(e.currentTarget.value);
        loadData(1, e.currentTarget.value, searchValue);
    };

    const handlePrevPage = () => {
        setCurrentPage(currentPage - 1);
        loadData(currentPage - 1, Number(perPage), searchValue);
    };

    const handleNextPage = () => {
        setCurrentPage(currentPage + 1);
        loadData(currentPage + 1, Number(perPage), searchValue);
    };

    return (
        <div className="sl__wrapper">
            {filterOpen &&
                <Filter
                    filters={filterOptions}
                    translate={translate}
                    selected={filters}
                    onApply={payload => {
                        setFilterOpen(false);
                        dispatchFilters({
                            type: SMARTLISTFILTERS.UPDATE,
                            payload
                        });
                        setActiveFilters((payload !== initialFilter));
                    }}
                    onCancel={() => setFilterOpen(false)}
                    onClose={() => setFilterOpen(false)}
                    onReset={() => {
                        dispatchFilters({
                            type: SMARTLISTFILTERS.DEFAULT
                        });
                        setActiveFilters(false);
                    }}
                />
            }
            <div className={hideHeader ? 'sl_header hide_this' : 'sl__header'}>
                <SearchInput
                    label="conversationsList"
                    setSearchValue={text => setSearchValue(text)}
                    placeholder={translate('search')}
                    searchValue={searchValue}
                />
                <div className={activeFilters ? 'sl__filterbtn is-active' : 'sl__filterbtn'} onClick={() => setFilterOpen(true)}>
                    {translate('filters')}
                </div>
            </div>
            <div className="sl__content">
                {filteredData().length === 0 && !loading && filters === initialFilter &&
                    <div className="conversationsList__scroll">
                        <div className="conversationsList__placeholder">
                            <p>{searchValue ? translate('no_search_results_found') : parse(translate('conversation_sl_no_account'))}</p>
                        </div>
                    </div>
                }
                {filteredData().length === 0 && !loading && filters !== initialFilter &&
                    <div className="conversationsList__scroll">
                        <div className="conversationsList__placeholder">
                            <p>{parse(translate('conversation_sl_no_result'))}</p>
                        </div>
                    </div>
                }
                {loading &&
                    <LoadingAnimation />
                }
                {!loading && filteredData().length > 0 && filteredData().map((item, key) => (
                    <SmartListItem
                        key={key}
                        item={item}
                    />
                ))}
            </div>
            <div className="sl__footer">
                <div className="">
                    <select className="sl__pagination__select" value={perPage} onChange={handlePerPageChange}>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="250">250</option>
                    </select>
                </div>
                <div className="sl__pagination">
                    <button type="button" disabled={currentPage === 1} onClick={handlePrevPage}>{icons.paginationLeft}</button>
                    <span>{currentPage}</span>
                    <button type="button" disabled={currentPage === pages} onClick={handleNextPage}>{icons.paginationRight}</button>
                </div>
            </div>
        </div>
    );
};

export default SmartListComponent;



// WEBPACK FOOTER //
// ./src/sections/SmartList/SmartListComponent.tsx